import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const BASE = process.env.PUBLIC_URL || '';

const APP_STORE_URL = 'https://apps.apple.com/app/id6758654084';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.softwarebeyondlimits.instantguide';

const SCREENSHOTS = [
  { src: `${BASE}/Sc01.png`, alt: 'Instant Guide screenshot: explore nearby landmarks' },
  { src: `${BASE}/Sc02.png`, alt: 'Instant Guide screenshot: audio tour in progress' },
  { src: `${BASE}/Sc03.png`, alt: 'Instant Guide screenshot: tour chapter view' },
];

const HERO_IMAGES = [
  { src: `${BASE}/IG001.jpg`, alt: 'Instant Guide app' },
  { src: `${BASE}/IG002.jpg`, alt: 'Instant Guide app' },
  { src: `${BASE}/IG003.jpg`, alt: 'Instant Guide app' },
  { src: `${BASE}/IG004.jpg`, alt: 'Instant Guide app' },
  { src: `${BASE}/IG005.jpg`, alt: 'Instant Guide app' },
];

function HeroCarousel({ images, intervalMs = 3000 }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [images.length, intervalMs]);

  return (
    <div className="hero-carousel">
      <div className="hero-carousel__frame">
        {images.map((img, i) => (
          <img
            key={i}
            src={img.src}
            alt={img.alt}
            className={`hero-carousel__img${i === current ? ' hero-carousel__img--active' : ''}`}
          />
        ))}
      </div>
      <div className="hero-carousel__dots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`hero-carousel__dot${i === current ? ' hero-carousel__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`View image ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: '🗺️',
    title: 'Explore',
    desc: 'See nearby landmarks on the map or in a list. Search by city or place name anywhere in the world.',
  },
  {
    icon: '🎧',
    title: 'Preview Free',
    desc: "Tap a place and start the tour. The introduction audio is free on every tour: sample the depth and style, then unlock the rest when you're ready.",
  },
  {
    icon: '🗣️',
    title: 'Like a Passionate Local',
    desc: "Your guide doesn't just list facts—he brings the place to life. Legends, scandals, and the details that stick. Unlock secrets and hidden gems you won't find in guidebooks. Warm, vivid, with wit and opinion, like a friend showing you around.",
  },
  {
    icon: '🎯',
    title: 'Categories That Go Deep',
    desc: 'Each tour is organized into rich categories: Stories, Secrets & Surprises, History, Art & Design, and Fun Facts. Plus unlockable hidden nuggets in each category—the kind of insider info that makes a place unforgettable.',
  },
  {
    icon: '✈️',
    title: 'Offline Ready',
    desc: 'Unlock a tour, download it, and listen without internet (perfect for roaming, flights, or low-signal areas).',
  },
  {
    icon: '🌐',
    title: 'Your Language',
    desc: 'Choose from English, Spanish, French, Chinese, Hindi, or Arabic. Full audio tours are in the language you select.',
  },
];

/* ============================================================
   SCREENSHOT SCROLL ANIMATION — staggered slide + scale + fade
   ============================================================ */
function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function ScreenshotSlides({ items, sectionRef }) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const section = sectionRef?.current;
    if (!section) return;

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const viewHeight = window.innerHeight;
        const scrollRange = viewHeight * 0.9;
        const raw = (viewHeight - rect.top) / scrollRange;
        const clamped = Math.min(1, Math.max(0, raw));
        setProgress(clamped);
        rafRef.current = null;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [sectionRef]);

  const n = items.length;
  return (
    <div className="screenshot-slides">
      {items.map((item, i) => {
        const start = (i / (n + 1)) * 0.7;
        const end = start + 0.5;
        const rawT = (progress - start) / (end - start);
        const t = easeOutCubic(Math.min(1, Math.max(0, rawT)));
        const translateY = (1 - t) * 72;
        const scale = 0.88 + 0.12 * t;
        const opacity = t;
        const translateX = (1 - t) * (i === 0 ? -24 : i === 1 ? 0 : 24);
        return (
          <div
            key={i}
            className="screenshot-slide-item screenshot-slide-item--animated"
            style={{
              opacity,
              transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
            }}
          >
            <img src={item.src} alt={item.alt} className="screenshot-slide-img" />
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   SCROLL ANIMATIONS
   ============================================================ */
function useScrollAnimations() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = document.querySelectorAll('.animate-on-scroll');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ============================================================
   INSTANT GUIDE PAGE
   ============================================================ */
function InstantGuide() {
  const screenshotsSectionRef = useRef(null);
  useScrollAnimations();

  return (
    <div className="detail-page">
      {/* ── Hero ── */}
      <section className="detail-hero detail-hero--ig">
        <div className="hero__orb hero__orb--blue" style={{ opacity: 0.1 }} />
        <div className="hero__orb hero__orb--purple" style={{ opacity: 0.1 }} />

        <div className="detail-hero__inner">
          <div className="detail-hero__grid">
            <div>
              <div className="detail-hero__app-icon detail-hero__app-icon--ig">
                <img src={`${BASE}/app_icon.jpg`} alt="Instant Guide" />
              </div>

              <h1 className="detail-hero__title">Instant Guide</h1>
              <p className="detail-hero__desc">
                Deep audio guides for every landmark. Tours up to one hour for over 11,000 places
                worldwide. Explore the history, culture, and stories behind the world's most
                fascinating places at your own pace, in your own language.
              </p>

              <div className="detail-hero__download-btns">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${BASE}/Download_on_the_App_Store_Badge_US.svg`}
                    alt="Download on the App Store"
                    className="btn-download-badge"
                  />
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${BASE}/GetItOnGooglePlay_Badge_Web_color_English.svg`}
                    alt="Get it on Google Play"
                    className="btn-download-badge"
                  />
                </a>
              </div>

              <div className="detail-hero__stats">
                <div className="detail-stat">
                  <div className="detail-stat__icon">🌍</div>
                  <div>
                    <div className="detail-stat__value">11,000+</div>
                    <div className="detail-stat__label">Places Worldwide</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">🎧</div>
                  <div>
                    <div className="detail-stat__value">Up to 1hr</div>
                    <div className="detail-stat__label">Per Tour</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">🌐</div>
                  <div>
                    <div className="detail-stat__value">6</div>
                    <div className="detail-stat__label">Languages</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero visual — carousel (3s per image) */}
            <div className="detail-hero__visual">
              <div className="detail-hero__screenshot-wrap detail-hero__screenshot-wrap--carousel">
                <HeroCarousel images={HERO_IMAGES} intervalMs={3000} />
              </div>
              <div className="detail-hero__glow detail-hero__glow--ig" />
            </div>
          </div>
        </div>

        <div className="hero__scroll-indicator">
          <div className="hero__scroll-dot" />
        </div>
      </section>

      {/* ── Why Instant Guide ── */}
      <section className="detail-features">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            Why <span className="gradient-text">Instant Guide</span>
          </h2>
          <p className="section-subtitle">
            Dive into stories, secrets, and hidden gems. Every tour is packed with
            bite-sized audio nuggets you can unlock; history, art, fun facts, and surprises.
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--ig">
                {f.icon}
              </div>
              <h3 className="feature-card__title">{f.title}</h3>
              <p className="feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Screenshots ── */}
      <section
        className="screenshots-bg-section"
        ref={screenshotsSectionRef}
        style={{ backgroundImage: `url(${BASE}/full_store_screenshots_bg.jpg)` }}
      >
        <ScreenshotSlides items={SCREENSHOTS} sectionRef={screenshotsSectionRef} />
      </section>


      {/* ── CTA ── */}
      <section className="detail-cta detail-cta--ig">
        <div className="detail-cta__orb" />
        <div className="detail-cta__inner">
          <h2 className="detail-cta__title">Unlock the World's Hidden Stories</h2>
          <p className="detail-cta__subtitle">
            Download Instant Guide and start discovering secrets, stories, and gems at every landmark. The introduction audio is always free.
          </p>
          <div className="detail-cta__btns">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src={`${BASE}/Download_on_the_App_Store_Badge_US.svg`}
                alt="Download on the App Store"
                className="btn-download-badge"
              />
            </a>
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
              <img
                src={`${BASE}/GetItOnGooglePlay_Badge_Web_color_English.svg`}
                alt="Get it on Google Play"
                className="btn-download-badge"
              />
            </a>
          </div>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/" className="btn-download btn-download--outline">
              ← Explore Other Apps
            </Link>
          </div>
          <div className="app-page-footer">
            <span>© {new Date().getFullYear()} Software Beyond Limits. All rights reserved.</span>
            <span className="app-page-footer__links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/apps/instant-guide/account-and-data">Account Policy</Link>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default InstantGuide;
